import { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Page, Layout, LegacyCard, EmptyState, LegacyStack, Text, ChoiceList, Button, Banner } from '@shopify/polaris';
import { getLosses, postAutoReconcileLosses } from '../../../services/lossesService';
import { getDepositById } from '../../../services/depositsService';
import ProductsWrapper from '../../UI/Products/ProductsWrapper';
import ProductLossesTable from '../../UI/Tables/ProductLossesTable';
import ProductFilters from '../../UI/Products/ProductFilters';
import ViewLossesPrimaryActions from '../../UI/Losses/ViewLossesPrimaryActions';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';

/**
 * Vista del informe de diferencias de inventario (cuenta 390).
 *
 * - Si la URL contiene `:id`, restringe al depósito (`/deposits/:id/losses`).
 * - Si no, lista cross-deposit (`/losses`).
 */
const ViewLosses = () => {
  const { id } = useParams();
  const navigate = useHistory();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);
  const [deposit, setDeposit] = useState(null);
  const [numbers, setNumbers] = useState({});
  // Qué se considera "se compensa solo": nada, la familia (cambio de talla) o la serie,
  // los 5 primeros caracteres de la family (cambio de color entre hermanas).
  const [netZeroMode, setNetZeroMode] = useState([]);
  const [reconciling, setReconciling] = useState(false);
  const [, dispatchToast] = useContext(ToastContext);
  const mode = netZeroMode[0] ?? null;

  const loadPage = (page = 1, currentFilters, rebuild = false, netZero = netZeroMode[0] ?? null) => {
    const params = { page, ...currentFilters };
    if (id) params.depositId = id;
    if (netZero) params.netZero = netZero;
    return getLosses(params).then((r) => {
      setData({ ...r.data, new: rebuild });
      setNumbers({
        netTotal: r.data.metadata.stockQty,
        distinctProducts: r.data.metadata.distinctProducts,
        distinctVariants: r.data.metadata.distinctVariants,
      });
    });
  };

  useEffect(() => {
    if (id) getDepositById(id).then((r) => setDeposit(r.data));
  }, [id]);

  useEffect(() => {
    if (filters) loadPage(1, filters, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, netZeroMode]);

  /**
   * Cuadra de golpe lo que se está viendo. Manda los MISMOS filtros que el listado para
   * que la acción no se salga de lo que el usuario tiene delante, y recarga al terminar
   * porque las familias cuadradas desaparecen del listado.
   */
  const handleAutoReconcile = () => {
    if (!mode) return;
    setReconciling(true);
    const params = { ...filters, netZero: mode };
    if (id) params.depositId = id;
    postAutoReconcileLosses(params)
      .then((r) => {
        dispatchToast({ type: SET_OK, payload: { message: r.data?.message || 'Reconciliado' } });
        return loadPage(1, filters, true);
      })
      .catch((e) =>
        dispatchToast({
          type: SET_ERROR,
          payload: { message: e?.response?.data?.message || 'Error al reconciliar' },
        }),
      )
      .finally(() => setReconciling(false));
  };

  const TableComponent = (args) => (
    <div className="losses_table">
      <ProductLossesTable {...args} deposit={deposit ?? undefined} />
    </div>
  );

  const title = id && deposit ? `Pérdidas — ${deposit.name}` : 'Pérdidas y apariciones';
  const subtitle = id ? 'Cuenta 390 en este depósito' : 'Cuenta 390 — todos los depósitos';

  return (
    <Page
      backAction={id ? { content: 'Ver depósito', onAction: () => navigate.goBack() } : undefined}
      title={title}
      subtitle={subtitle}
      primaryAction={<ViewLossesPrimaryActions depositId={id} depositName={deposit?.name} />}
      divider
    >
      <Layout>
        <Layout.Section>
          <LegacyCard>
            {Object.keys(numbers).length > 0 && (
              <LegacyCard.Section>
                <LegacyStack distribution="fill">
                  <span>
                    <Text variant="headingXs" weight="semibold">
                      Neto
                    </Text>
                    <Text
                      variant="heading3xl"
                      tone={
                        (numbers.netTotal ?? 0) < 0 ? 'critical' : (numbers.netTotal ?? 0) > 0 ? 'success' : 'subdued'
                      }
                    >
                      {numbers.netTotal ?? 0}
                    </Text>
                  </span>
                  <span>
                    <Text variant="headingXs" weight="semibold">
                      Series afectadas
                    </Text>
                    <Text variant="heading3xl">{numbers.distinctProducts}</Text>
                  </span>
                  <span>
                    <Text variant="headingXs" weight="semibold">
                      SKU
                    </Text>
                    <Text variant="heading3xl">{numbers.distinctVariants}</Text>
                  </span>
                </LegacyStack>
              </LegacyCard.Section>
            )}
            <LegacyCard.Section title="Filtros">
              <ProductFilters onFilterSelected={(f) => setFilters(f)} />
              <div style={{ marginTop: '1rem' }}>
                <ChoiceList
                  title="Diferencias que se compensan solas"
                  choices={[
                    { label: 'Todas', value: '' },
                    {
                      label: 'Solo las que se compensan solas',
                      value: 'family',
                      helpText:
                        'Familias con tallas que faltan y tallas que sobran en la misma cantidad: cambio de talla no reportado.',
                    },
                    {
                      label: 'Solo las que se compensan por serie',
                      value: 'serie',
                      helpText:
                        'Familias hermanas (mismos 5 primeros caracteres) que se compensan entre ellas: cambio de color sin registrar.',
                    },
                  ]}
                  selected={netZeroMode.length ? netZeroMode : ['']}
                  onChange={(v) => setNetZeroMode(v[0] ? v : [])}
                />
              </div>
              {mode && (
                <div style={{ marginTop: '1rem' }}>
                  <Banner status="info">
                    <LegacyStack alignment="center" distribution="equalSpacing">
                      <Text variant="bodyMd">
                        {data?.metadata?.count
                          ? `${data.metadata.count} ${
                              mode === 'serie' ? 'familias en series' : 'familias'
                            } que se compensan solas con los filtros actuales.`
                          : 'Nada que cuadrar con los filtros actuales.'}
                      </Text>
                      <Button
                        onClick={handleAutoReconcile}
                        loading={reconciling}
                        disabled={!data?.metadata?.count}
                      >
                        Reconciliar todas
                      </Button>
                    </LegacyStack>
                  </Banner>
                </div>
              )}
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section>
          {data && !data.data.length && (
            <LegacyCard>
              <LegacyCard.Section>
                <EmptyState
                  heading={
                    mode
                      ? 'Ninguna se compensa sola con esos filtros'
                      : 'No hay diferencias registradas con esos filtros'
                  }
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                ></EmptyState>
              </LegacyCard.Section>
            </LegacyCard>
          )}
          {data && (
            <ProductsWrapper
              data={data}
              TableComponent={TableComponent}
              loadPage={() => loadPage(data.metadata.page + 1, filters)}
            />
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default ViewLosses;
